/** Sprint 5-d PR-4 · AI Chat API client（session CRUD + available-tools + fetch-stream SSE）。 */
import axios from '../../../../api/axios';
import i18n from '../../../../i18n';

// E5.1 §2.2：聊天請求帶使用者在系統選的語系（後端 _lang 優先用它·非瀏覽器 Accept-Language）
const uiLangHeader = (): Record<string, string> => ({ 'X-UI-Lang': i18n.language || 'zh-TW' });

const BASE = '/api/ee/ai-chat';

export interface ChatSession {
  id: number;
  title: string | null;
  session_scope: string | null;
  model: string | null;
  message_count: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cache_read_tokens: number;
  total_cost_ntd: number;
  is_pinned: boolean;
  is_archived: boolean;
  last_message_at: string | null;
  created_at: string;
}

export interface ChatMessage {
  id: number;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  tool_calls?: unknown;
  tool_use_id?: string | null;
  input_tokens: number;
  output_tokens: number;
  cost_ntd: number;
  actual_provider?: string | null;
  actual_model?: string | null;
  fallback_reason?: string | null;
  created_at: string;
}

export interface ToolCategory {
  name: string;
  tools: {
    name: string; label: string; description: string; requires_confirmation: boolean;
    display_name?: Record<string, string>;   // Scope 10（backend #285）·缺則 fallback label/name
  }[];
}

export interface AiMode {
  enabled_ui_modes: string[];
  can_enable_chat: boolean;
  block_reason: string | null;
}

export interface TenantAiConfig {
  org_id: number;
  mode: string;
  default_model: string;
  escalation_model: string;
  prompt_cache_enabled: boolean;
  allow_heavy_tools: boolean;
  fallback_chain: string[];
  enabled_tools: string[];
  disabled_tools: string[];
  monthly_budget_ntd: number | null;
  budget_alert_percent: number | null;
  can_enable_chat?: boolean;
  block_reason?: string | null;
}

export interface ChatScope {
  func_code: string;
  tool_count: number;
}

export const listSessions = async (archived = false, scope?: string): Promise<ChatSession[]> =>
  (await axios.get<ChatSession[]>(`${BASE}/sessions`, {
    params: { archived, ...(scope ? { scope } : {}) },
  })).data;

// §4.5：可進入範圍對話的功能清單（前端判斷哪些功能磚可對話）
export const getScopes = async (): Promise<ChatScope[]> =>
  (await axios.get<ChatScope[]>(`${BASE}/scopes`)).data;

export const createSession = async (body: { title?: string; session_scope?: string } = {}): Promise<ChatSession> =>
  (await axios.post<ChatSession>(`${BASE}/sessions`, body)).data;

export interface PendingAction {
  id: number; tool_name: string; summary: string; status: string;
  params?: Record<string, unknown>; expires_at?: string | null; created_at?: string | null;
}

export const getSession = async (id: number): Promise<{
  session: ChatSession; messages: ChatMessage[]; pending_actions?: PendingAction[]; tool_calls?: unknown[];
}> => (await axios.get(`${BASE}/sessions/${id}`)).data;

export const patchSession = async (
  id: number, body: { title?: string; is_pinned?: boolean; is_archived?: boolean },
): Promise<ChatSession> => (await axios.patch<ChatSession>(`${BASE}/sessions/${id}`, body)).data;

export const deleteSession = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/sessions/${id}`);
};

export const getCost = async (id: number): Promise<{ total_cost_ntd: number; message_count: number }> =>
  (await axios.get(`${BASE}/sessions/${id}/cost`)).data;

export const getAvailableTools = async (sessionScope?: string): Promise<{ categories: ToolCategory[] }> =>
  (await axios.get(`${BASE}/available-tools`, { params: sessionScope ? { session_scope: sessionScope } : {} })).data;

// ── 租戶 AI 模式（/api/system/tenant-profile/ai-mode）+ 設定（/api/ee/tenant-ai-config）─────
export const getAiMode = async (): Promise<AiMode> =>
  (await axios.get('/api/system/tenant-profile/ai-mode')).data;

export const putAiMode = async (enabled_ui_modes: string[]): Promise<{ enabled_ui_modes: string[] }> =>
  (await axios.put('/api/system/tenant-profile/ai-mode', { enabled_ui_modes })).data;

export const getTenantAiConfig = async (): Promise<TenantAiConfig> =>
  (await axios.get('/api/ee/tenant-ai-config')).data;

export const putTenantAiConfig = async (body: Partial<TenantAiConfig>): Promise<TenantAiConfig> =>
  (await axios.put('/api/ee/tenant-ai-config', body)).data;

export const getTenantAiUsage = async (month?: string): Promise<{
  period: string; currency: string; total_cost: string; monthly_budget_ntd: number | null;
  budget_alert_percent: number | null; line_items: unknown[];
}> => (await axios.get('/api/ee/tenant-ai-config/usage', { params: month ? { month } : {} })).data;

// ── SSE 串流（fetch + ReadableStream·EventSource 無法帶 auth / POST body）─────
export interface ConfirmRequired {
  pending_action_id: number;
  tool_name?: string;
  display_name?: Record<string, string>;
  summary: string;
  params?: Record<string, unknown>;
  expires_at?: string;
}

export interface StreamHandlers {
  onStart?: (d: { provider?: string; model?: string; expired_action_ids?: number[] }) => void;
  onDelta?: (text: string) => void;
  onToolUse?: (d: { tool_use_id?: string; name?: string; input?: unknown }) => void;
  onToolResult?: (d: { tool_use_id?: string; content?: unknown }) => void;
  onConfirmRequired?: (d: ConfirmRequired) => void;   // E5 §5.1
  onActionResult?: (d: { status?: string; message?: string; code?: string; action_id?: number }) => void;
  onFallback?: (d: { actual_provider?: string; actual_model?: string; reason?: string }) => void;
  onStop?: (d: { message_id?: number; cost_ntd?: number; input_tokens?: number; output_tokens?: number }) => void;
  onError?: (d: { code?: string; detail?: string }) => void;
}

// E5 §3.2：確認 / 取消待確認操作（不呼叫 AI·不計費）
export const confirmAction = async (sessionId: number, actionId: number): Promise<{ status: string; message?: string; code?: string }> =>
  (await axios.post(`${BASE}/sessions/${sessionId}/actions/${actionId}/confirm`, null, { headers: uiLangHeader() })).data;
export const cancelAction = async (sessionId: number, actionId: number): Promise<{ status: string; message?: string; code?: string }> =>
  (await axios.post(`${BASE}/sessions/${sessionId}/actions/${actionId}/cancel`, null, { headers: uiLangHeader() })).data;

export interface VoiceMeta {
  input_channel?: 'text' | 'voice';
  stt_text?: string | null;
  stt_lang?: string | null;
}

export const streamMessage = async (
  sessionId: number, content: string, handlers: StreamHandlers, signal?: AbortSignal,
  meta?: VoiceMeta,
): Promise<void> => {
  const base = axios.defaults.baseURL || '';
  const token = localStorage.getItem('session_id') || localStorage.getItem('access_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...uiLangHeader() };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    headers['X-Txn-Token'] = localStorage.getItem('txn_token') || token;
  }
  let resp: Response;
  try {
    resp = await fetch(`${base}${BASE}/sessions/${sessionId}/messages`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        content,
        ...(meta?.input_channel ? { input_channel: meta.input_channel } : {}),
        ...(meta?.stt_text != null ? { stt_text: meta.stt_text } : {}),
        ...(meta?.stt_lang != null ? { stt_lang: meta.stt_lang } : {}),
      }),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') return;
    handlers.onError?.({ detail: String(e) });
    return;
  }
  if (!resp.ok || !resp.body) {
    handlers.onError?.({ detail: `HTTP ${resp.status}` });
    return;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    let chunk;
    try { chunk = await reader.read(); } catch { return; }   // 中斷屬正常路徑（token 後端已計）
    if (chunk.done) break;
    buf += decoder.decode(chunk.value, { stream: true });
    let sep;
    while ((sep = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7);
        else if (line.startsWith('data: ')) data += line.slice(6);
      }
      if (!data) continue;
      let parsed: Record<string, unknown>;
      try { parsed = JSON.parse(data); } catch { continue; }
      if (event === 'message_start') handlers.onStart?.(parsed as never);
      else if (event === 'text_delta') handlers.onDelta?.(String(parsed.delta || ''));
      else if (event === 'tool_use') handlers.onToolUse?.(parsed as never);
      else if (event === 'tool_result') handlers.onToolResult?.(parsed as never);
      else if (event === 'confirm_required') handlers.onConfirmRequired?.(parsed as never);
      else if (event === 'action_result') handlers.onActionResult?.(parsed as never);
      else if (event === 'fallback_notice') handlers.onFallback?.(parsed as never);
      else if (event === 'message_stop') handlers.onStop?.(parsed as never);
      else if (event === 'error') handlers.onError?.(parsed as never);
    }
  }
};
